import { indexes } from "./common"
import {
    LazyIterator,
    type FilterFn,
    type FilterMapFn,
    type FlatMapFn,
    type FoldFn,
    type MapFn,
    type ReduceFn,
} from "./sync"

export type AsyncFilterFn<T> = ((value: T) => Promise<boolean>) | FilterFn<T>
export type AsyncMapFn<T, U> = ((value: T) => Promise<U>) | MapFn<T, U>
export type AsyncFilterMapFn<T, U> =
    | ((value: T) => Promise<[true, U] | false | [false] | [false, ...unknown[]]>)
    | FilterMapFn<T, U>
export type AsyncFlatMapFn<T, U> = ((value: T) => Promise<AsyncIterable<U>>) | FlatMapFn<T, U>
export type AsyncReduceFn<T> = ((acc: T, value: T) => Promise<T>) | ReduceFn<T>
export type AsyncFoldFn<T, U> = ((acc: U, value: T) => Promise<U>) | FoldFn<T, U>
type Flatten<T> = T extends AsyncIterable<infer AsyncItem> ? AsyncItem : T extends Iterable<infer Item> ? Item : T
type Defined<T> = T extends undefined ? never : T
type Some<T> = T extends null ? never : T

export abstract class BaseLazyAsyncIterator<T> implements AsyncIterableIterator<T> {
    [Symbol.asyncIterator](): this {
        return this
    }

    abstract next(): Promise<IteratorResult<T, undefined>>

    async skip(n: number): Promise<this> {
        for (let i = 0; i < n; i++) await this.next()
        return this
    }

    async nth(n: number): Promise<IteratorResult<T, undefined>> {
        for (let i = 1; i < n; i++) await this.next()
        return this.next()
    }

    take(n: number): AsyncTakeIterator<T> {
        return new AsyncTakeIterator(this, n)
    }

    repeat(): AsyncRepeatIterator<T> {
        return new AsyncRepeatIterator(this)
    }

    map<U>(fn: AsyncMapFn<T, U>): AsyncMapIterator<T, U> {
        return new AsyncMapIterator(this, fn)
    }

    filter(fn: AsyncFilterFn<T>): AsyncFilterIterator<T> {
        return new AsyncFilterIterator(this, fn)
    }

    flat(): AsyncFlatIterator<T> {
        return new AsyncFlatIterator(this)
    }

    group(size: number): AsyncGroupIterator<T> {
        return new AsyncGroupIterator(this, size)
    }

    zip<U>(other: Iterable<U> | AsyncIterable<U>): AsyncZipIterator<T, U> {
        return new AsyncZipIterator(this, other)
    }

    permute<U>(other: Iterable<U>): AsyncPermuteIterator<T, U> {
        return new AsyncPermuteIterator(this, other)
    }

    chain(other: Iterator<T> | AsyncIterator<T>): AsyncChainIterator<T> {
        return new AsyncChainIterator(this, other)
    }

    filterMap<U>(fn: AsyncFilterMapFn<T, U>): AsyncFilterMapIterator<T, U> {
        return new AsyncFilterMapIterator(this, fn)
    }

    zipMap<U>(fn: MapFn<T, U>): AsyncZipMapIterator<T, U> {
        return new AsyncZipMapIterator(this, fn)
    }

    filterZipMap<U>(fn: FilterMapFn<T, U>): AsyncFilterZipMapIterator<T, U> {
        return new AsyncFilterZipMapIterator(this, fn)
    }

    flatMap<U>(fn: AsyncFlatMapFn<T, U>): AsyncFlatMapIterator<T, U> {
        return new AsyncFlatMapIterator(this, fn)
    }

    defined(): AsyncFilterIterator<Defined<T>> {
        return this.filter((v) => v !== undefined) as AsyncFilterIterator<Defined<T>>
    }

    some(): AsyncFilterIterator<Some<T>> {
        return this.filter((v) => v !== null) as AsyncFilterIterator<Some<T>>
    }

    enumerate(): AsyncZipIterator<T, number> {
        return this.zip(indexes())
    }

    async any(fn: AsyncFilterFn<T>): Promise<boolean> {
        for await (const item of this) {
            if (await fn(item)) return true
        }

        return false
    }

    async all(fn: AsyncFilterFn<T>): Promise<boolean> {
        for await (const item of this) {
            if (!(await fn(item))) return false
        }

        return true
    }

    async first(fn: AsyncFilterFn<T>): Promise<[true, T] | [false]> {
        for await (const item of this) {
            if (await fn(item)) return [true, item]
        }

        return [false]
    }

    async position(fn: AsyncFilterFn<T>): Promise<number | null> {
        let i = 0

        for await (const item of this) {
            if (await fn(item)) return i
            i++
        }

        return null
    }

    async collect(): Promise<T[]> {
        const out: T[] = []

        for await (const item of this) {
            out.push(item)
        }

        return out
    }

    async count(fn: FilterFn<T> | AsyncFilterFn<T>): Promise<number> {
        let count = 0

        for await (const item of this) {
            if (fn(item)) count++
        }

        return count
    }

    async reduce(fn: ReduceFn<T> | AsyncReduceFn<T>): Promise<[true, T] | [false]> {
        const next = await this.next()
        if (next.done) return [false]
        let acc = next.value

        for await (const item of this) {
            acc = await fn(acc, item)
        }

        return [true, acc]
    }

    async fold<U>(fn: FoldFn<T, U> | AsyncFoldFn<T, U>, init: U): Promise<U> {
        let acc = init

        for await (const item of this) {
            acc = await fn(acc, item)
        }

        return acc
    }
}

export class LazyAsyncIterator<T> extends BaseLazyAsyncIterator<T> {
    private internal: Iterator<T> | AsyncIterator<T>
    constructor(iterable: Iterable<T> | AsyncIterable<T>) {
        super()
        this.internal = Symbol.iterator in iterable ? iterable[Symbol.iterator]() : iterable[Symbol.asyncIterator]()
    }

    static from<Obj extends {}>(obj: Obj): LazyAsyncIterator<[string, Obj[keyof Obj]]> {
        return new LazyAsyncIterator(Object.entries(obj))
    }

    async next(): Promise<IteratorResult<T, undefined>> {
        return await this.internal.next()
    }
}

class AsyncTakeIterator<T> extends BaseLazyAsyncIterator<T> {
    constructor(
        private internal: AsyncIterator<T>,
        private n: number,
    ) {
        super()
    }

    async next(): Promise<IteratorResult<T, undefined>> {
        if (this.n--) return await this.internal.next()
        return { done: true, value: undefined }
    }
}

class AsyncRepeatIterator<T> extends BaseLazyAsyncIterator<T> {
    private loop: T[] = []
    private i = 0

    constructor(private internal: BaseLazyAsyncIterator<T> | null) {
        super()
    }

    async next(): Promise<IteratorResult<T, undefined>> {
        if (this.internal) {
            const next = await this.internal.next()

            if (next.done) this.internal = null
            else {
                this.loop.push(next.value)
                return next
            }
        }

        if (this.loop.length === 0) return { done: true, value: undefined }
        if (this.i === this.loop.length) this.i = 0
        const i = this.i++
        return { done: false, value: this.loop[i] }
    }
}

class AsyncMapIterator<T, U> extends BaseLazyAsyncIterator<U> {
    constructor(
        private internal: AsyncIterator<T>,
        private fn: AsyncMapFn<T, U>,
    ) {
        super()
    }

    async next(): Promise<IteratorResult<U, undefined>> {
        const next = await this.internal.next()
        if (next.done) return next
        return { done: false, value: await this.fn(next.value) }
    }
}

class AsyncFilterIterator<T> extends BaseLazyAsyncIterator<T> {
    constructor(
        private internal: AsyncIterator<T>,
        private fn: AsyncFilterFn<T>,
    ) {
        super()
    }

    async next(): Promise<IteratorResult<T, undefined>> {
        const next = await this.internal.next()
        if (next.done) return next
        if (await this.fn(next.value)) return next
        return this.next()
    }
}

class AsyncFlatIterator<T> extends BaseLazyAsyncIterator<Flatten<T>> {
    private buf: Iterator<T> = [][Symbol.iterator]()

    constructor(private internal: AsyncIterator<T>) {
        super()
    }

    async next(): Promise<IteratorResult<Flatten<T>, undefined>> {
        let bufNext = this.buf.next()

        while (bufNext.done) {
            const next = await this.internal.next()
            if (next.done) return next

            if (next.value && typeof next.value === "object" && Symbol.iterator in next.value) {
                this.buf = (next.value as Iterable<T>)[Symbol.iterator]()
            } else return next as IteratorResult<Flatten<T>, undefined>

            bufNext = this.buf.next()
        }

        return bufNext as IteratorResult<Flatten<T>, undefined>
    }
}
class AsyncGroupIterator<T> extends BaseLazyAsyncIterator<T[]> {
    constructor(
        private internal: AsyncIterator<T>,
        private size: number,
    ) {
        super()
    }

    async next(): Promise<IteratorResult<T[], undefined>> {
        const buf = []

        while (true) {
            const next = await this.internal.next()

            if (next.done) {
                if (buf.length > 0) {
                    return { done: false, value: buf }
                }
                return next
            }

            buf.push(next.value)

            if (buf.length === this.size) {
                return { done: false, value: buf }
            }
        }
    }
}

class AsyncZipIterator<T, U> extends BaseLazyAsyncIterator<[T, U]> {
    private other: Iterator<U> | AsyncIterator<U>

    constructor(
        private internal: AsyncIterator<T>,
        other: Iterable<U> | AsyncIterable<U>,
    ) {
        super()
        this.other = Symbol.asyncIterator in other ? other[Symbol.asyncIterator]() : other[Symbol.iterator]()
    }

    async next(): Promise<IteratorResult<[T, U], undefined>> {
        const internalNext = await this.internal.next()
        if (internalNext.done) return internalNext
        const otherNext = await this.other.next()
        if (otherNext.done) return otherNext
        return { done: false, value: [internalNext.value, otherNext.value] }
    }
}

class AsyncPermuteIterator<T, U> extends BaseLazyAsyncIterator<[T, U]> {
    private other: Iterator<U> | AsyncIterator<U>
    private otherEnded = false
    private current: T | null = null
    private buf: U[] = []
    private bufIdx = 0

    constructor(
        private internal: AsyncIterator<T>,
        other: Iterable<U> | AsyncIterable<U>,
    ) {
        super()
        this.other = Symbol.asyncIterator in other ? other[Symbol.asyncIterator]() : other[Symbol.iterator]()
    }

    async next(): Promise<IteratorResult<[T, U], undefined>> {
        if (!this.current) {
            const next = await this.internal.next()
            if (next.done) return next
            this.current = next.value
            this.bufIdx = 0
        }

        if (!this.otherEnded) {
            const next = await this.other.next()
            if (!next.done) {
                this.buf.push(next.value)
                return { done: false, value: [this.current, next.value] }
            }
            this.otherEnded = true
            this.current = null
            return this.next()
        }

        if (this.bufIdx >= this.buf.length) {
            this.current = null
            return this.next()
        }

        return { done: false, value: [this.current, this.buf[this.bufIdx++]] }
    }
}

class AsyncChainIterator<T> extends BaseLazyAsyncIterator<T> {
    constructor(
        private internal: AsyncIterator<T> | null,
        private other: Iterator<T> | AsyncIterator<T>,
    ) {
        super()
    }

    async next(): Promise<IteratorResult<T, undefined>> {
        if (!this.internal) return this.other.next()

        const next = await this.internal.next()

        if (next.done) {
            this.internal = null
            return this.other.next()
        }

        return next
    }
}

class AsyncFilterMapIterator<T, U> extends BaseLazyAsyncIterator<U> {
    constructor(
        private internal: AsyncIterator<T>,
        private fn: AsyncFilterMapFn<T, U>,
    ) {
        super()
    }

    async next(): Promise<IteratorResult<U, undefined>> {
        const next = await this.internal.next()
        if (next.done) return next

        const tryMapped = await this.fn(next.value)
        if (tryMapped === false) return this.next()
        const [filter, mapped] = tryMapped
        if (filter) return { done: false, value: mapped }
        return this.next()
    }
}

class AsyncZipMapIterator<T, U> extends BaseLazyAsyncIterator<[T, U]> {
    constructor(
        private internal: AsyncIterator<T>,
        private fn: AsyncMapFn<T, U>,
    ) {
        super()
    }

    async next(): Promise<IteratorResult<[T, U], undefined>> {
        const next = await this.internal.next()
        if (next.done) return next
        return { done: false, value: [next.value, await this.fn(next.value)] }
    }
}

class AsyncFilterZipMapIterator<T, U> extends BaseLazyAsyncIterator<[T, U]> {
    constructor(
        private internal: AsyncIterator<T>,
        private fn: AsyncFilterMapFn<T, U>,
    ) {
        super()
    }

    async next(): Promise<IteratorResult<[T, U], undefined>> {
        const next = await this.internal.next()
        if (next.done) return next
        const tryMapped = await this.fn(next.value)
        if (tryMapped === false) return this.next()
        const [filter, mapped] = tryMapped
        if (filter) return { done: false, value: [next.value, mapped] }
        return this.next()
    }
}

class AsyncFlatMapIterator<T, U> extends BaseLazyAsyncIterator<U> {
    private buf: Iterator<U> | AsyncIterator<U> = [][Symbol.iterator]()
    constructor(
        private internal: AsyncIterator<T>,
        private fn: AsyncFlatMapFn<T, U>,
    ) {
        super()
    }

    async next(): Promise<IteratorResult<U, undefined>> {
        let bufNext = await this.buf.next()

        while (bufNext.done) {
            const next = await this.internal.next()
            if (next.done) return next
            const otherIter = await this.fn(next.value)
            this.buf = Symbol.iterator in otherIter ? otherIter[Symbol.iterator]() : otherIter[Symbol.asyncIterator]()
            bufNext = await this.buf.next()
        }

        return bufNext
    }
}

export class CallbackIterator<T> extends BaseLazyAsyncIterator<T> {
    private buf: IteratorResult<T, undefined>[] = []
    private queue: ((next: IteratorResult<T, undefined>) => void)[] = []
    private isDone = false
    public timedOut = false

    readonly callback = (next: IteratorResult<T, undefined>) => {
        if (next.done) {
            this.markDone()
        } else if (this.queue.length > 0) {
            this.queue.splice(0, 1)[0](next)
        } else {
            this.buf.push(next)
        }
    }

    constructor(private timeoutSeconds?: number | null) {
        super()
    }

    async next(): Promise<IteratorResult<T, undefined>> {
        if (this.isDone) return { done: true, value: undefined }

        if (this.buf.length > 0) {
            const next = this.buf.splice(0, 1)[0]
            if (next.done) this.isDone = true
            return next
        }

        return new Promise((resolve, _) => {
            if (this.timeoutSeconds) {
                const handle = setTimeout(() => {
                    this.markDone()
                    this.timedOut = true
                }, this.timeoutSeconds * 1000)

                this.queue.push((next) => {
                    clearTimeout(handle)
                    resolve(next)
                })
            } else this.queue.push(resolve)
        })
    }

    private markDone(): void {
        this.isDone = true

        for (const resolver of this.queue) {
            resolver({ done: true, value: undefined })
        }
    }
}

export class PipeIterator<T> extends BaseLazyAsyncIterator<T> {
    private buf: T[] = []
    private queue: ((next: IteratorResult<T, undefined>) => void)[] = []
    private isDone = false
    public timedOut = false

    constructor(fn: (receive: (...data: T[]) => void) => Promise<void>) {
        super()
        fn((...data) => {
            data.reverse()
            while (this.queue.length > 0) {
                const next = data.pop()
                if (next) this.queue.splice(0, 1)[0]({ done: false, value: next })
                else break
            }
            data.reverse()
            this.buf.push(...data)
        }).then((_) => this.markDone())
    }

    async next(): Promise<IteratorResult<T, undefined>> {
        if (this.buf.length > 0) {
            const next = this.buf.splice(0, 1)[0]
            return { done: false, value: next }
        }

        if (this.isDone) return { done: true, value: undefined }

        return new Promise((resolve, _) => {
            this.queue.push(resolve)
        })
    }

    private markDone(): void {
        this.isDone = true

        for (const resolver of new LazyIterator(this.queue).skip(this.buf.length)) {
            resolver({ done: true, value: undefined })
        }
    }
}
